<script setup>
import PlayerButton from './PlayerButton.vue';

defineProps({
    presets: Array[Object],
    selectedPreset: Object,
});

</script>

<template>
    <div class="root">
        <h3 id="error-message" v-show="errorMessage">{{ errorMessage }}</h3>

        <div class="player-buttons">
            <PlayerButton
                size="30px"
                @click="$emit('previousPresetClick')"
                type="previouspreset"
                :disabled="sequencerDisabled || !allSoundsLoaded"
            />
            <PlayerButton
                @click="onPlayButtonClick"
                type="play"
                :is-playing="isPlaying"
                :disabled="sequencerDisabled || !allSoundsLoaded"
            />
            <PlayerButton
                size="30px"
                @click="$emit('nextPresetClick')"
                type="nextpreset"
                :disabled="sequencerDisabled || !allSoundsLoaded"
            />
        </div>

        <ul>
            <li
                :data-disabled="sequencerDisabled || !allSoundsLoaded"
                v-for="section in sections"
                class="noselect"
                :class="{
                    current: currentSection === section,
                    cued: cuedSection === section,
                }"
                @click="onSectionButtonClick(section)"
            >
                {{ section }}
            </li>
        </ul>

        <!--<div class="loading" v-if="selectedPreset && !allSoundsLoaded">Loading samples...</div>-->
        <div class="loading" v-html="selectedPreset && !allSoundsLoaded ? 'Loading samples...' : '&nbsp;'"></div>
    </div>
</template>

<style lang="scss" scoped>

ul {
    list-style: none;
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding: 0;
    flex-wrap: wrap;
    gap: 1rem;
    position: relative;
    max-width: 700px;
}

@keyframes border-pulsate {
    0% {
        border-color: #bf8763;
    }
    50% {
        border-color: rgba(191, 135, 99, 0.2);
    }
    100% {
        border-color: #bf8763;
    }
}

@keyframes pulsate {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}

li {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    padding: 0;
    width: 60px;
    aspect-ratio: 1/1;
    color: #fff;
    /*
    transition: color 1s, background-image 1s, opacity 1s;
     */

    font-size: 14px;

    border: 2px solid #fff;
    border-radius: 50%;

    -webkit-tap-highlight-color:transparent; /* Prevent blue highlighting rectangle on Chrome mobile */

    &:hover {
        cursor: pointer;
        background-color: rgba(255, 255, 255, 0.1);
    }
}

li[data-disabled='true'] {
    pointer-events: none;
    color: #8c8c8c;
    border-color: #8c8c8c;
}

li.current {
    transition: color 0s, background-color 0s, background-size 0.5s;
    background-color: #E05829;

    &:hover {
        background-color: #FD642F;
    }
}

li.cued {
    /*
    background: url(../assets/chord-button-selected.png) no-repeat;

     */
    /* background-color: #262626; */
    animation: pulsate 2s infinite;
    animation: border-pulsate 2s infinite;
    /*
    transition: color 0s, background-color 0s;
     */

    /*
    background-color: #E05829;
    */
    /*
    background-color: #1cc79e;
    */
}

div.loading {
    margin-top: 10px;
    color: #888888;
}

.player-buttons {
    display: flex;
    align-items: center;
    gap: 5px;
}

button:disabled {
    cursor: default;
}

.noselect {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}

#error-message {
    color: orangered;
}

</style>

<script>
import Sequencer from '../audio/sequencer.js';
import { toRaw } from 'vue';
const sequencer = (window.sequencer = new Sequencer());

const DEBUGGING_ENABLED = false;

export default {
    emits: ['previousPresetClick', 'nextPresetClick'],
    created() {
        window.addEventListener('sectionChange', this.onSectionChange.bind(this), false);
        window.addEventListener('sectionStart', this.onSectionStart.bind(this), false);
        window.addEventListener('soundsLoaded', this.onSoundsLoaded.bind(this), false);
        window.addEventListener('sequencerDisabledChange', this.onSequencerDisabledChange.bind(this), false);

        document.addEventListener('keydown', this.onKeyDown);
    },
    destroyed() {
        window.removeEventListener('sectionChange', this.onSectionChange, false);
        window.removeEventListener('sectionStart', this.onSectionStart, false);
        window.removeEventListener('soundsLoaded', this.onSoundsLoaded, false);
        window.removeEventListener('sequencerDisabledChange', this.onSequencerDisabledChange, false);

        document.removeEventListener('keydown', this.onKeyDown);
    },
    watch: {
        allSoundsLoaded(newState) {
            this.sequencerDisabled = !newState;
        }
    },
    computed: {},
    data() {
        return {
            sections: [],
            currentSection: null,
            cuedSection: null,

            isPlaying: false,
            allSoundsLoaded: false,
            sequencerDisabled: true,
            autoPlay: false,
            sectionStartTime: 0,
            nextSectionTime: 0,
            errorMessage: '',
        };
    },
    expose: ['loadPreset'],
    methods: {
        loadPreset(preset, autoPlay = false) {
            this.allSoundsLoaded = false;

            this.errorMessage = !preset.tempo || !preset.numBars || !preset.numBarsTail ? 'Invalid or missing meta data for this preset! Please correct in Google sheet.' : '';
            if (this.errorMessage) return;

            this.sections = preset.sections;

            // TODO: Bevor etwas kaputt geht beim Wechseln von Presets w�hrend dem Playback, Playback stoppen. Aber auch hier gibt es Probleme
            // wenn man w�hrend dem Fadeout noch wechselt. Das checken.
            if (this.selectedPreset && this.isPlaying) {
                this.onPlayButtonClick();
            }
            sequencer.loadPreset(preset);

            this.autoPlay = autoPlay;
        },
        onSectionButtonClick(section) {
            if (toRaw(this.currentSection) === toRaw(section)) {
                this.isPlaying = false;
                this.currentSection = null;
                this.cuedSection = null;

                //sequencer.stopAfterCurrentSection();
                sequencer.stop();

                return;
            }

            this.cueSection(section);

            if (!this.isPlaying) {
                this.isPlaying = true;
                sequencer.play();
            }
        },

        cueSection(section) {
            this.cuedSection = section;
            sequencer.cueSection(section);
        },

        getPreviousOrNextSection(offset) {
            let sectionIndex = this.sections.indexOf(this.cuedSection || this.currentSection);
            sectionIndex = Math.min(Math.max(sectionIndex + offset, 0), this.sections.length - 1);
            return this.sections[sectionIndex];
        },

        onPlayButtonClick() {
            this.isPlaying = !this.isPlaying;

            if (this.isPlaying) {
                this.cueSection(this.sections[0]);
            } else {
                this.currentSection = null;
                this.cuedSection = null;
            }

            sequencer.togglePlay();
        },

        onSectionChange(event) {
            DEBUGGING_ENABLED && console.debug('section changes');
            this.currentSection = event.detail.newSection;
            this.cuedSection = null;
        },

        onSectionStart(event) {
            this.currentSection = event.detail.newSection;
            this.cuedSection = null;
            DEBUGGING_ENABLED && console.debug('section starts');

            const time = new Date().getTime();
            this.sectionStartTime = time;
            this.nextSectionTime = time + event.detail.sectionDuration * 1000;
        },

        onSoundsLoaded() {
            this.allSoundsLoaded = true;

            // Falls aktuell spielende und gecuete Section in neuem Preset nicht existiert, zur�cksetzen
            if (this.currentSection && !this.sections.includes(this.currentSection)) {
                this.currentSection = null;
            }

            if (this.cuedSection && !this.sections.includes(this.cuedSection)) {
                this.cuedSection = null;
            }

            // Allow parent website to resize the iFrame according to the content
            if (window.parent) {
                window.parent.postMessage({
                    message: 'presetLoaded'
                }, "*");
            }

            // If autoPlay is true, start playing as soon as sounds are loaded
            if (this.autoPlay && !this.isPlaying) {
                // TODO: Das funktioniert noch nicht richtig. Es gibt Aussetzer bzw. es braucht ein Delay.
                //this.onPlayButtonClick();
            }
        },

        onSequencerDisabledChange(event) {
            this.sequencerDisabled = event.detail;
        },

        onKeyDown(event) {
            if (this.sequencerDisabled) return;

            if (event.key === ' ') {
                this.onPlayButtonClick();
                event.preventDefault();
            } else if (event.key === 'ArrowLeft') {
                if (this.isPlaying) {
                    const section = this.getPreviousOrNextSection(-1);
                    if (section) {
                        this.cueSection(section);
                    }
                }
                event.preventDefault();
            } else if (event.key === 'ArrowRight') {
                if (this.isPlaying) {
                    const section = this.getPreviousOrNextSection(1);
                    if (section) {
                        this.cueSection(section);
                    }
                }
                event.preventDefault();
            }
        },
    },
};
</script>
